import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
} from "@mui/material";
import { Helmet } from "react-helmet";
import DraftsIcon from "@mui/icons-material/Drafts";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";

export default function AddSong() {
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("1");
  const [song, setSong] = useState("");
  const [songnumber, setSongNumber] = useState("");
  const [author, SetAuthor] = useState("");
  const [engtitle, setEngTitle] = useState("");
  const [engLyrics, setEngLyrics] = useState("");
  const [copyright, setCopyright] = useState("no");
  const API_URL = process.env.REACT_APP_API_URL;

  const initialSection = new URLSearchParams(window.location.search).get('section') || 'Glocal Prayer Network';
  const [customSection, setCustomSection] = useState(initialSection);
  const [createdSections, setCreatedSections] = useState(() => {
    try {
      const stored = localStorage.getItem("gpn_songs_custom_sections");
      const parsed = stored ? JSON.parse(stored) : [];
      const names = parsed.map(p => p.name).filter(Boolean);
      return Array.from(new Set(["Glocal Prayer Network", ...names, initialSection]));
    } catch {
      return ["Glocal Prayer Network", initialSection];
    }
  });

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    severity: 'warning'
  });
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('songs'));
  const [activeDraftId, setActiveDraftId] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/get-song-number`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      } else {
        setSongNumber(data.index_number);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Auto-save draft on page close / unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (title.trim() || song.trim()) {
        DraftManager.saveDraft('songs', {
          title, language, song, songnumber, author, engtitle, engLyrics, copyright, customSection
        }, activeDraftId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, [title, language, song, songnumber, author, engtitle, engLyrics, copyright, customSection, activeDraftId]);

  const handleManualSaveDraft = () => {
    if (!title.trim() && !song.trim()) {
      setToast({ open: true, message: "Please enter a song title or lyrics before saving draft.", severity: "warning" });
      return;
    }
    const res = DraftManager.saveDraft('songs', {
      title, language, song, songnumber, author, engtitle, engLyrics, copyright, customSection
    }, activeDraftId);

    if (res.success) {
      setActiveDraftId(res.draftId);
      setDraftsList(res.drafts || []);
      setToast({ open: true, message: "Song draft saved successfully!", severity: "success" });
    } else {
      setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
    }
  };

  const handleContinueDraft = (draft) => {
    if (!draft || !draft.data) return;
    const d = draft.data;
    if (d.title !== undefined) setTitle(d.title);
    if (d.language !== undefined) setLanguage(d.language);
    if (d.song !== undefined) setSong(d.song);
    if (d.songnumber !== undefined) setSongNumber(d.songnumber);
    if (d.author !== undefined) SetAuthor(d.author);
    if (d.engtitle !== undefined) setEngTitle(d.engtitle);
    if (d.engLyrics !== undefined) setEngLyrics(d.engLyrics);
    if (d.copyright !== undefined) setCopyright(d.copyright);
    if (d.customSection !== undefined) setCustomSection(d.customSection);
    setActiveDraftId(draft.id);
    setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
  };

  const handleDeleteDraft = (draftId) => {
    const res = DraftManager.deleteDraft('songs', draftId);
    setDraftsList(res.drafts || []);
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft removed.", severity: "info" });
  };

  const handleAddSong = async (replaceDuplicate = false) => {
    const token = localStorage.getItem('token');
    if (title === '' || song === '' || songnumber === '') {
      setToast({ open: true, message: "Song title, lyrics, and song number are required fields", severity: "warning" });
      return false;
    }
    try {
      const response = await fetch(`${API_URL}add-song`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          language,
          song,
          songnumber,
          author,
          eng_title: engtitle,
          eng_lyrics: engLyrics,
          copyright,
          custom_section: customSection,
          replace_duplicate: replaceDuplicate === true
        })
      });

      const data = await response.json();

      // Check if duplicate detected
      if (response.status === 409 || data?.isDuplicate) {
        setConfirmDialog({
          open: true,
          title: "Song Already Exists",
          message: data?.message || `A song titled "${title}" is already in the songbook. Would you like to replace that song with this one?`,
          severity: "warning",
          confirmText: "Yes, Replace Song",
          cancelText: "No, Cancel",
          onConfirm: () => {
            setConfirmDialog(prev => ({ ...prev, open: false }));
            handleAddSong(true);
          },
          onCancel: () => {
            setConfirmDialog(prev => ({ ...prev, open: false }));
          }
        });
        return;
      }

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        setToast({ open: true, message: "Failed: " + (data?.message || 'Unknown error'), severity: "error" });
        return;
      }

      if (activeDraftId) {
        DraftManager.deleteDraft('songs', activeDraftId);
      }
      setToast({
        open: true,
        message: replaceDuplicate ? "Duplicate song replaced successfully!" : "New song added successfully!",
        severity: "success"
      });
      setTimeout(() => {
        window.location.href = '/admin/songs';
      }, 1200);
    } catch (err) {
      console.error("Error adding song:", err);
      setToast({ open: true, message: "Network error adding song", severity: "error" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Add Song - GPN</title>
      </Helmet>

      <Box display="flex" justifyContent="center" mt={4} mb={6}>
        <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: "900px", borderRadius: 3 }}>
          {/* Header with Draft Buttons */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1.5}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Add New Song
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add songs to the global songbook for mobile app worship.
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleManualSaveDraft}
                sx={{
                  color: '#64d2e2',
                  borderColor: 'rgba(76, 161, 175, 0.4)',
                  bgcolor: 'rgba(76, 161, 175, 0.1)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: 'rgba(76, 161, 175, 0.25)',
                    borderColor: '#4CA1AF',
                  },
                }}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<DraftsIcon sx={{ fontSize: 18 }} />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('songs'));
                  setDraftsOpen(true);
                }}
                sx={{
                  bgcolor: 'rgba(76, 161, 175, 0.3)',
                  border: '1px solid rgba(76, 161, 175, 0.6)',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: 'rgba(76, 161, 175, 0.5)',
                  },
                }}
              >
                Drafts ({draftsList.length}/3)
              </Button>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={2} justifyContent="space-between">
            <Box flex="1 1 31%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Song Collection / Section *</Typography>
                <HelpTooltip
                  title="Song Collection"
                  purpose="Organizes this song into a specific collection (e.g. Glocal Prayer Network, Daily Devotions)."
                  howItWorks="Choose an existing collection or assign to Glocal Prayer Network."
                  mobileEffect="Users can browse songs grouped by their respective collection."
                />
              </Box>
              <FormControl fullWidth size="small">
                <Select value={customSection} onChange={(e) => setCustomSection(e.target.value)}>
                  {createdSections.map(sec => (
                    <MenuItem key={sec} value={sec}>{sec}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box flex="1 1 31%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Language *</Typography>
                <HelpTooltip
                  title="Song Language"
                  purpose="Specifies the primary language in which the song was written and is sung."
                  howItWorks="Select from English, Telugu, Hindi, or other available languages."
                  mobileEffect="Users in the mobile app can filter and browse songs by this selected language."
                />
              </Box>
              <FormControl fullWidth required size="small">
                <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <MenuItem value="1">English</MenuItem>
                  <MenuItem value="2">Telugu</MenuItem>
                  <MenuItem value="3">Hindi</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box flex="1 1 31%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Song Number *</Typography>
                <HelpTooltip
                  title="Song Index Number"
                  purpose="Unique sequence identifier for the hymn in the hymnal collection."
                  howItWorks="Automatically allocated based on the highest existing hymn number."
                  mobileEffect="Allows mobile users to quickly search and jump to this song by entering its number."
                />
              </Box>
              <TextField
                disabled
                size="small"
                value={songnumber}
                required
                fullWidth
                onChange={(e) => setSongNumber(e.target.value)}
              />
            </Box>

            <Box flex="1 1 100%" mt={1}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Song Title *</Typography>
                <HelpTooltip
                  title="Song Title"
                  purpose="Primary name or opening line of the hymn / worship song."
                  howItWorks="Enter the exact title in the native language."
                  mobileEffect="Displayed as the primary headline in the mobile app songbook and search results."
                />
              </Box>
              <TextField
                size="small"
                value={title}
                placeholder="Enter song title..."
                required
                fullWidth
                onChange={(e) => setTitle(e.target.value)}
              />
            </Box>
          </Box>

          {/* Song Lyrics */}
          <Box mt={2.5}>
            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
              <Typography variant="subtitle2" fontWeight={600}>Song Lyrics *</Typography>
              <HelpTooltip
                title="Song Lyrics"
                purpose="Complete verses and chorus of the song in its native language."
                howItWorks="Paste stanzas and refrains line by line."
                mobileEffect="Rendered in easy-to-read typography on mobile screens during corporate and individual worship."
              />
            </Box>
            <TextField
              multiline
              rows={6}
              fullWidth
              placeholder="Paste song verses and chorus here..."
              value={song}
              required
              onChange={(e) => setSong(e.target.value)}
              inputProps={{ "data-testid": "song-lyrics" }}
            />
          </Box>

          {/* Optional English Title & Lyrics for non-English songs */}
          {(language === '2' || language === '3') && (
            <Box mt={2.5} p={2} sx={{ bgcolor: 'rgba(76, 161, 175, 0.06)', borderRadius: 2, border: '1px dashed rgba(76, 161, 175, 0.3)' }}>
              <Box mb={2}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <Typography variant="subtitle2" fontWeight={600}>English Title (Optional)</Typography>
                  <HelpTooltip
                    title="English Transliteration / Title"
                    purpose="Provides an English transliteration or translation of the non-English song title."
                    howItWorks="Enter how the title sounds or translates in English."
                    mobileEffect="Enables English-speaking worshippers to discover and sing along."
                  />
                </Box>
                <TextField
                  size="small"
                  type="text"
                  fullWidth
                  placeholder="e.g. Yehova Naa Kaapari..."
                  value={engtitle}
                  onChange={(e) => setEngTitle(e.target.value)}
                />
              </Box>

              <Box>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <Typography variant="subtitle2" fontWeight={600}>English Lyrics / Transliteration</Typography>
                  <HelpTooltip
                    title="English Lyrics"
                    purpose="Romanized or translated verses for believers learning the song."
                    howItWorks="Type or paste the English script representation of the lyrics."
                    mobileEffect="Displayed under a tab or toggle in the mobile app song viewer."
                  />
                </Box>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="English script lyrics..."
                  value={engLyrics}
                  onChange={(e) => setEngLyrics(e.target.value)}
                />
              </Box>
            </Box>
          )}

          <Box display="flex" flexWrap="wrap" gap={2} justifyContent="space-between" mt={2.5}>
            <Box flex="1 1 48%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Author / Songwriter</Typography>
                <HelpTooltip
                  title="Author Attribution"
                  purpose="Credits the hymn writer, composer, or ministry."
                  howItWorks="Enter the composer/author's name."
                  mobileEffect="Displayed as attribution metadata beneath the song title in the app."
                />
              </Box>
              <TextField
                size="small"
                type="text"
                placeholder="e.g. Charles Wesley, Paul Wilbur"
                value={author}
                fullWidth
                onChange={(e) => SetAuthor(e.target.value)}
              />
            </Box>

            <Box flex="1 1 48%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Copyright Protected? *</Typography>
                <HelpTooltip
                  title="Copyright Status"
                  purpose="Flags whether this hymn has active copyright restrictions or is public domain."
                  howItWorks="Select Yes if copyrighted by a publisher/CCLI, No if Public Domain."
                  mobileEffect="Controls copyright notice display and sharing restrictions in the mobile app."
                />
              </Box>
              <RadioGroup
                row
                value={copyright}
                onChange={(e) => setCopyright(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio size="small" />} label="No (Public Domain)" />
              </RadioGroup>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 4, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <Button variant="outlined" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleAddSong} sx={{ background: '#177373' }}>
              Save Song
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Drafts Modal */}
      <DraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        moduleName="Songs"
        drafts={draftsList}
        onContinueEditing={handleContinueDraft}
        onDeleteDraft={handleDeleteDraft}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        severity={confirmDialog.severity}
      />

      <Toast toast={toast} setToast={setToast} />
    </>
  );
}
